import React, { useEffect, useMemo, useState } from "react";
import { IconAddPhotoAlternate, IconDeleteForeverBlue } from "assets/svg";
import {
  Button,
  Input,
  message,
  Radio,
  Select,
  Upload,
  Image,
  UploadFile,
  UploadProps,
  InputNumber,
} from "antd";
import {
  DISPOSAL_TYPE,
  MAX_FILE_SIZE,
  PRODUCT_CATEGORY,
  PRODUCT_STATE,
  SHIP_CHARGE_TYPE,
  SHIP_DAY,
} from "common/constants";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { uploadCleanData, uploadImageMultiple } from "reducers/uploadSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { useCommonContext } from "contexts/CommonContext";
import {
  editProduct,
  fetchProductDetail,
  productCleanData,
} from "reducers/productSlice";
import { FileData, FileType, Product, ProductInfo } from "common/interfaces";
import { getBase64 } from "common/utils";
import { isEmpty } from "lodash";
import { FILE_BASE_URL } from "config/apiConfig";
import CustomConfirmModal from "components/CustomConfirmModal";
import { useDeleteProductCompanyMutation } from "reducers/productApi";
import { ProductStatusEnum } from "common/enums/Product.enum";

const style: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const EditProduct = () => {
  const { setHeaderTitle } = useCommonContext();
  const dispatch = useDispatch<AppDispatch>();
  const { uploadImageMultipleData, error: uploadErr } = useSelector(
    (state: RootState) => state.upload,
  );
  const {
    productDetailData,
    createProductData,
    editProductData,
    error: productErr,
  } = useSelector((state: RootState) => state.product);
  const productDetailInfo: Product | undefined = productDetailData?.data;
  const { TextArea } = Input;
  const controller = new AbortController();
  const navigate = useNavigate();
  const defaultValues: ProductInfo = {
    product_name: "",
    category_id: undefined,
    quantity_value: undefined,
    disposal_type: "0",
    state: undefined,
    description: "",
    shipping_charge_type: undefined,
    shipping_days: undefined,
    is_free: false,
    price: undefined,
    status: 1,
    image_urls: [],
  };
  const { handleSubmit, control, watch, reset } = useForm<ProductInfo>({
    defaultValues,
  });
  const { productCode } = useParams();
  const { setProductInfoContext, fileList, setFileList } = useCommonContext();
  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [hasError, setHasError] = useState(false);
  const [openConfirmModalSuspend, setOpenConfirmModalSuspend] = useState(false);
  const [openConfirmModalSubmit, setOpenConfirmModalSubmit] = useState(false);
  const [openConfirmModalDelete, setOpenConfirmModalDelete] = useState(false);
  const MAX_FILES = 5;

  const productName: string = watch("product_name");
  const categoryId = watch("category_id");
  const disposalType: string = watch("disposal_type");
  const quantityValue = watch("quantity_value");
  const state = watch("state");
  const description: string = watch("description");
  const shippingChargeType = watch("shipping_charge_type");
  const shippingDays = watch("shipping_days");
  const isFree: boolean = watch("is_free");
  const price = watch("price");

  const image_urls: string[] = useMemo(() => {
    const normalizeUrl = (url: string) => url.replace(/^.*\/public/, "public");
    const productImages =
      fileList
        ?.map((img) => (img?.url ? normalizeUrl(img.url) : undefined))
        .filter((url): url is string => Boolean(url)) ?? []; // lọc bỏ undefined
    const uploadedImages = !isEmpty(uploadImageMultipleData)
      ? uploadImageMultipleData?.data.map((i: FileData) => normalizeUrl(i.path))
      : [];
    if (productImages.length && uploadedImages.length) {
      return [...productImages, ...uploadedImages];
    }
    return uploadedImages.length ? uploadedImages : productImages;
  }, [fileList, uploadImageMultipleData]);

  const productInfo: ProductInfo = {
    product_name: productName,
    category_id: categoryId,
    quantity_value: quantityValue,
    disposal_type: disposalType,
    state: state,
    description: description,
    shipping_charge_type: shippingChargeType,
    shipping_days: shippingDays,
    is_free: isFree,
    price: !isFree ? price : undefined,
    status: 1,
    image_urls: image_urls,
  };

  const [deleteProductCompany] = useDeleteProductCompanyMutation();

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as FileType);
    }
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };

  const handleChange: UploadProps["onChange"] = ({ fileList: newFileList }) =>
    setFileList(newFileList);

  const beforeUpload = (file: File, fileListInEvent: File[]) => {
    if (!file.type.startsWith("image/")) {
      message.error(`${file.name} 有効な画像ファイルではありません`);
      return Upload.LIST_IGNORE;
    }
    const currentTotalSize = (fileList ?? []).reduce(
      (sum, f) =>
        sum + (f.originFileObj instanceof File ? f.originFileObj.size : 0),
      0,
    );
    const newFilesTotalSize = fileListInEvent.reduce(
      (sum, f) => sum + f.size,
      0,
    );
    const totalSize = currentTotalSize + newFilesTotalSize;
    if (totalSize > MAX_FILE_SIZE) {
      message.error("写真の合計サイズが10MBを超えています");
      return Upload.LIST_IGNORE;
    }
    if ((fileList?.length ?? 0) + fileListInEvent.length > MAX_FILES) {
      message.error(`アップロードできるのは ${MAX_FILES} 画像`);
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const onSuspend = () => {
    const formData = new FormData();
    fileList?.forEach((file: any) => {
      const actualFile = file.originFileObj;
      if (actualFile) {
        formData.append("files", actualFile);
      }
    });
    if (!isEmpty(fileList)) {
      dispatch(uploadImageMultiple(formData, "product", controller.signal));
      setOpenConfirmModalSuspend(false);
    }
  };

  const onSubmit = () => {
    const formData = new FormData();
    fileList?.forEach((file: any) => {
      const actualFile = file.originFileObj;
      if (actualFile) {
        formData.append("files", actualFile);
      }
    });
    if (!isEmpty(fileList)) {
      dispatch(uploadImageMultiple(formData, "product", controller.signal));
      setIsSubmit(true);
      setOpenConfirmModalSubmit(false);
    } else {
      setHasError(true);
      message.error("写真を選択してください");
    }
  };

  const onDelete = async () => {
    if (productCode) {
      await deleteProductCompany([productCode]).unwrap();
      message.success("削除に成功しました");
      setOpenConfirmModalDelete(false);
      navigate("/seller/product/list");
    }
  };

  useEffect(() => {
    if (productCode) {
      dispatch(fetchProductDetail(productCode, controller.signal));
    }
  }, [productCode]);

  useEffect(() => {
    if (uploadImageMultipleData?.success && productDetailInfo && productCode) {
      if (isSubmit) {
        setProductInfoContext(productInfo);
        dispatch(editProduct(productCode, productInfo, controller.signal));
        message.success("製品の更新に成功しました");
      }
      if (!isSubmit) {
        dispatch(
          editProduct(
            productCode,
            { ...productInfo, status: ProductStatusEnum.SUSPENDED },
            controller.signal,
          ),
        );
        message.success("製品の一時停止に成功しました");
      }
    }
  }, [uploadImageMultipleData, productDetailInfo, isSubmit]);

  useEffect(() => {
    if (createProductData?.success) {
      message.success("製品の作成に成功しました");
      reset({});
      setFileList([]);
      setProductInfoContext(null);
      dispatch(productCleanData());
      dispatch(uploadCleanData());
      navigate("/seller/product/list");
    }
  }, [createProductData]);

  useEffect(() => {
    if (editProductData?.success && productCode) {
      reset({});
      setFileList([]);
      setProductInfoContext(null);
      dispatch(productCleanData());
      dispatch(uploadCleanData());
      dispatch(fetchProductDetail(productCode, controller.signal));
    }
  }, [editProductData]);

  useEffect(() => {
    if (uploadErr) {
      message.error(uploadErr);
    }
  }, [uploadErr]);

  useEffect(() => {
    if (productErr) {
      message.error(productErr);
    }
  }, [productErr]);

  useEffect(() => {
    if (productDetailInfo) {
      const imageFiles: UploadFile[] =
        productDetailInfo.images?.map((img) => ({
          uid: img.id.toString(),
          name: img.image_url.split("/").pop() || `image-${img.id}.jpg`,
          status: "done",
          url: `${FILE_BASE_URL}/${img.image_url}`,
        })) ?? [];
      setFileList(imageFiles);
      const images =
        productDetailInfo.images?.map((img) => img.image_url) ?? [];
      reset({
        product_name: productDetailInfo.product_name,
        category_id: productDetailInfo.category_id.toString(),
        quantity_value: productDetailInfo.quantity_value,
        disposal_type: productDetailInfo.disposal_type.toString() || "0",
        state: productDetailInfo.state.toString(),
        description: productDetailInfo.description,
        shipping_charge_type: productDetailInfo.shipping_charge_type.toString(),
        shipping_days: productDetailInfo.shipping_days.toString(),
        is_free: productDetailInfo.is_free === 1,
        price:
          (!productDetailInfo.is_free && productDetailInfo.price) || undefined,
        status: 1,
        image_urls: images,
      });
    }
  }, [productDetailInfo]);

  useEffect(() => {
    dispatch(uploadCleanData());
    dispatch(productCleanData());
    setHeaderTitle("出品内容の編集");
    return () => {
      setProductInfoContext(null);
      setFileList([]);
      dispatch(uploadCleanData());
      dispatch(productCleanData());
    };
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full">
      <div className="self-stretch w-full min-h-[924px] p-[20px] pb-[100px] md:p-10 bg-white inline-flex flex-col justify-start items-start gap-[30px] md:gap-12 overflow-hidden">
        <div className="self-stretch flex flex-col justify-start items-start gap-[30px] md:gap-10">
          <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
            <div
              data-プロパティ1="base + text"
              className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
            >
              <div className="w-1 self-stretch bg-primary rounded-sm" />
              <div className="flex-1 flex justify-start items-center gap-2.5">
                <div className="flex justify-start items-center gap-[5px]">
                  <div className="justify-start text-primary text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                    出品画像
                  </div>
                  <div className="justify-start text-primary text-[14px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                    （最大5枚）
                  </div>
                </div>
              </div>
            </div>
            {previewImage && (
              <Image
                wrapperStyle={{ display: "none" }}
                preview={{
                  visible: previewOpen,
                  onVisibleChange: (visible) => setPreviewOpen(visible),
                  afterOpenChange: (visible) => !visible && setPreviewImage(""),
                }}
                src={previewImage}
              />
            )}
            <Upload
              multiple
              listType="picture-card"
              fileList={fileList}
              onPreview={handlePreview}
              onChange={handleChange}
              beforeUpload={beforeUpload}
              className="w-full"
              style={{ width: "100%", height: "100%", border: "none" }}
            >
              <div className="w-full self-stretch bg-white flex flex-col justify-start items-start gap-3.5">
                <div
                  className={`self-stretch md:p-12 bg-slate-50 rounded-[6px] md:rounded-xl flex flex-col justify-start items-center gap-5 overflow-hidden md:border border-dashed ${
                    hasError ? "border-red-dark" : "border-primary"
                  }`}
                >
                  <div className="hidden self-stretch md:flex flex-col justify-start items-center gap-2.5">
                    <div className="self-stretch text-center justify-start text-primary text-xl font-bold font-['Noto_Sans_JP'] leading-loose">
                      写真をドラッグして追加
                    </div>
                    <div className="self-stretch text-center justify-start text-black text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                      商品の全体、詳細、文字入りの写真ををアップロードしてください
                    </div>
                  </div>
                  <div className="hidden md:block self-stretch text-center justify-start text-black text-sm font-normal font-['Noto_Sans_JP'] leading-snug">
                    ー または ー
                  </div>
                  <div
                    data-プロパティ1="base + icon"
                    className="h-10 min-w-full md:min-w-24 px-5 bg-primary rounded-md inline-flex justify-center items-center gap-[5px] overflow-hidden"
                  >
                    <IconAddPhotoAlternate className="w-[20px] h-[20px] aspect-[1/1]" />
                    <div className="text-center justify-start text-white text-sm font-bold font-['Noto_Sans_JP'] leading-snug">
                      画像を選択する
                    </div>
                  </div>
                </div>
              </div>
            </Upload>
          </div>
          <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
            <div
              data-プロパティ1="base"
              className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
            >
              <div className="w-1 self-stretch bg-primary rounded-sm" />
              <div className="flex-1 flex justify-start items-center gap-2.5">
                <div className="flex justify-start items-center gap-[5px]">
                  <div className="justify-start text-primary text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                    商品の基本情報
                  </div>
                </div>
              </div>
            </div>
            <div className="self-stretch bg-white flex flex-col justify-start items-start gap-3.5">
              <div className="w-full max-w-[800px] flex flex-col justify-start items-start gap-[20px] md:gap-5">
                <div className="flex-col md:flex-row self-stretch inline-flex justify-start items-start gap-[7px] md:gap-5">
                  <div className="w-44 md:py-3 flex justify-start items-start gap-2.5">
                    <div className="justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                      商品名
                    </div>
                  </div>
                  <div className="w-full flex-1 inline-flex flex-col justify-center items-start gap-[5px]">
                    <Controller
                      control={control}
                      name="product_name"
                      render={({ field }) => (
                        <Input
                          required
                          showCount
                          maxLength={40}
                          {...field}
                          placeholder="例：廃プラスチック、金属くず、木くず etc."
                          className="max-w-[604px] w-full h-[50px]"
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="flex-col md:flex-row self-stretch inline-flex justify-start items-start md:items-center gap-[7px] md:gap-5">
                  <div className="w-44 flex justify-start items-center gap-2.5">
                    <div className="justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                      カテゴリー
                    </div>
                  </div>
                  <Controller
                    control={control}
                    name="category_id"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        allowClear
                        options={Object.values(PRODUCT_CATEGORY)}
                        placeholder="カテゴリーを選択してください"
                        className="max-w-[604px] w-full h-[50px]"
                      />
                    )}
                  />
                </div>
                <div className="flex-col md:flex-row self-stretch inline-flex justify-start items-start md:items-center gap-[7px] md:gap-5">
                  <div className="w-44 flex justify-start items-start gap-2.5">
                    <div className="justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                      数量（Kg）
                    </div>
                  </div>
                  <div className="max-w-[604px] w-full flex flex-col md:flex-row gap-[10px]">
                    <Controller
                      control={control}
                      name="quantity_value"
                      rules={{ required: true }}
                      render={({ field }) => (
                        <InputNumber
                          required
                          {...field}
                          min={0}
                          placeholder="例：1.3 Kg"
                          className="w-full md:w-[200px] [&_.ant-input-number-input]:h-[50px]"
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
            <div
              data-プロパティ1="base"
              className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
            >
              <div className="w-1 self-stretch bg-primary rounded-sm" />
              <div className="flex-1 flex justify-start items-center gap-2.5">
                <div className="flex justify-start items-center gap-[5px]">
                  <div className="justify-start text-primary text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                    出品詳細
                  </div>
                </div>
              </div>
            </div>
            <div className="self-stretch bg-white flex flex-col justify-start items-start gap-3.5">
              <div className="w-full max-w-[800px] flex flex-col justify-start items-start gap-5">
                <div className="flex-col md:flex-row self-stretch min-h-12 inline-flex justify-start items-start md:items-center gap-5">
                  <div className="w-44 flex justify-start items-start gap-2.5">
                    <div className="justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                      商品名
                    </div>
                  </div>
                  <div className="flex-1 inline-flex flex-col justify-center items-start gap-[5px]">
                    <div className="self-stretch flex flex-col md:flex-row md:inline-flex justify-start items-start gap-[15px] md:gap-7 flex-wrap content-start">
                      <Controller
                        control={control}
                        name="disposal_type"
                        render={({ field }) => (
                          <Radio.Group
                            {...field}
                            options={Object.values(DISPOSAL_TYPE)}
                            defaultValue="0"
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
                <div className="self-stretch flex-col md:flex-row md:inline-flex justify-start items-center gap-[7px] md:gap-5">
                  <div className="w-44 flex justify-start items-center gap-2.5">
                    <div className="justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                      商品の状態
                    </div>
                  </div>
                  <Controller
                    control={control}
                    name="state"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        allowClear
                        options={Object.values(PRODUCT_STATE)}
                        placeholder="状態を選択する"
                        className="max-w-[604px] w-full h-[50px]"
                      />
                    )}
                  />
                </div>
                <div className="self-stretch flex-col md:flex-row md:inline-flex justify-start items-start gap-5">
                  <div className="w-44 py-3 flex justify-start items-start gap-2.5">
                    <div className="justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                      商品の説明
                    </div>
                  </div>
                  <Controller
                    control={control}
                    name="description"
                    render={({ field }) => (
                      <TextArea
                        required
                        {...field}
                        rows={6}
                        placeholder="例：臭気があります／形状にばらつきあり etc."
                        className="w-[604px]"
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
            <div
              data-プロパティ1="base"
              className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
            >
              <div className="w-1 self-stretch bg-primary rounded-sm" />
              <div className="flex-1 flex justify-start items-center gap-2.5">
                <div className="flex justify-start items-center gap-[5px]">
                  <div className="justify-start text-primary text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                    配送について
                  </div>
                </div>
              </div>
            </div>
            <div className="self-stretch bg-white flex flex-col justify-start items-start gap-3.5">
              <div className="w-full max-w-[800px] flex flex-col justify-start items-start gap-5">
                <div className="self-stretch flex-col md:flex-row md:inline-flex justify-start items-center gap-5">
                  <div className="w-44 flex justify-start items-center gap-2.5">
                    <div className="justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                      配送量の負担
                    </div>
                  </div>
                  <Controller
                    control={control}
                    name="shipping_charge_type"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        allowClear
                        options={Object.values(SHIP_CHARGE_TYPE)}
                        placeholder="選択してください"
                        className="max-w-[604px] w-full h-[50px]"
                      />
                    )}
                  />
                </div>
                <div className="self-stretch flex-col md:flex-row md:inline-flex justify-start items-center gap-5">
                  <div className="w-44 flex justify-start items-center gap-2.5">
                    <div className="justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                      発送までの日数
                    </div>
                  </div>
                  <Controller
                    control={control}
                    name="shipping_days"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        allowClear
                        options={Object.values(SHIP_DAY)}
                        placeholder="選択してください"
                        className="max-w-[604px] w-full h-[50px]"
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
            <div
              data-プロパティ1="base"
              className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
            >
              <div className="w-1 self-stretch bg-primary rounded-sm" />
              <div className="flex-1 flex justify-start items-center gap-2.5">
                <div className="flex justify-start items-center gap-[5px]">
                  <div className="justify-start text-primary text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                    販売価格
                  </div>
                </div>
              </div>
            </div>
            <div className="self-stretch bg-white flex flex-col justify-start items-start gap-3.5">
              <div className="w-full max-w-[800px] flex flex-col justify-start items-start gap-5">
                <div className="self-stretch inline-flex justify-start items-center gap-5">
                  <div className="flex-1 inline-flex flex-col justify-start items-start gap-3.5">
                    <Controller
                      control={control}
                      name="is_free"
                      render={({ field }) => (
                        <Radio.Group
                          style={style}
                          {...field}
                          options={[
                            { value: true, label: "無料で提供する" },
                            { value: false, label: "価格を設定する" },
                          ]}
                          defaultValue={false}
                        />
                      )}
                    />
                    <Controller
                      disabled={isFree}
                      control={control}
                      name="price"
                      render={({ field }) => (
                        <InputNumber
                          required
                          {...field}
                          min={0}
                          max={999000000}
                          prefix="￥"
                          parser={(value) => {
                            const numeric = value?.replace(/[^\d]/g, "") || "0";
                            return Number(numeric);
                          }}
                          onKeyPress={(e) => {
                            if (!/[0-9]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          className="max-w-[604px] w-full h-[50px]"
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
          <div className="self-stretch justify-start">
            <span className="text-black text-[15px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
              禁止されている行為及び出品物を必ずご確認ください。
              <br />
              また、
            </span>
            <span className="text-black text-base font-normal font-['Noto_Sans_JP'] underline leading-relaxed">
              利用規約
            </span>
            <span className="text-black text-[15px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
              {" "}
              及び{" "}
            </span>
            <span className="text-black text-base font-normal font-['Noto_Sans_JP'] underline leading-relaxed">
              プライバシーポリシー
            </span>
            <span className="text-black text-[15px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
              {" "}
              に同意の上、「この内容で出品する」ボタンを押してください。
            </span>
          </div>
          <div className="self-stretch flex flex-col justify-start items-start gap-3.5">
            <div className="self-stretch flex flex-col-reverse md:flex-row md:inline-flex justify-start items-start gap-3.5">
              <Button
                block
                type="primary"
                color="danger"
                variant="outlined"
                htmlType="button"
                className="max-w-[340px] min-h-[60px] text-lg font-bold"
                onClick={() => setOpenConfirmModalSuspend(true)}
              >
                出品を一時停止する
              </Button>
              <Button
                block
                type="primary"
                htmlType="button"
                className="max-w-[340px] min-h-[60px] bg-primary text-lg font-bold"
                onClick={() => setOpenConfirmModalSubmit(true)}
              >
                変更する
              </Button>
            </div>
            <div
              data-プロパティ1="base + icon"
              className="inline-flex justify-start items-center gap-[3px] cursor-pointer"
              onClick={() => setOpenConfirmModalDelete(true)}
            >
              <IconDeleteForeverBlue className="w-[20px] h-[20px] aspect-[1/1]" />
              <div className="justify-start text-blue-900 text-sm font-medium font-['Noto_Sans_JP'] underline leading-snug">
                この商品を削除する
              </div>
            </div>
          </div>
        </div>
      </div>
      <CustomConfirmModal
        open={openConfirmModalSuspend}
        title="この出品を一時停止しますか？"
        description={`出品を一時停止すると、
          購入希望者からこの商品は表示・検索されなくなります。`}
        onOk={onSuspend}
        onCancel={() => setOpenConfirmModalSuspend(false)}
        okText="出品を一時停止する"
        cancelText="戻る"
        okButtonBgColor="bg-primary"
      />
      <CustomConfirmModal
        open={openConfirmModalSubmit}
        title="この内容で変更を保存しますか？"
        description={`商品の出品を確定すると、購入希望者が
          この情報を閲覧・問い合わせできるようになります。
          内容に誤りがないか、今一度ご確認ください。`}
        onOk={onSubmit}
        onCancel={() => setOpenConfirmModalSubmit(false)}
        okText="この内容で変更する"
        cancelText="戻る"
        okButtonBgColor="bg-primary"
      />
      <CustomConfirmModal
        open={openConfirmModalDelete}
        title="この商品を削除してもよろしいですか？"
        description={`出品情報を削除すると、この商品は出品一覧から完全に削除され、
          購入希望者からも閲覧できなくなります。`}
        note={`
          ※削除後は、出品内容や履歴を復元することはできません。
          ※進行中の取引がある場合は、先にキャンセル処理を行ってください。`}
        onOk={onDelete}
        onCancel={() => setOpenConfirmModalDelete(false)}
        okText="完全に削除する"
        cancelText="戻る"
        okButtonBgColor="bg-red-dark"
      />
    </form>
  );
};

export default EditProduct;
